import React, { useMemo} from 'react';
import { useNavigate} from 'react-router-dom';
import { useApiQuery, useAuth, useAlarms, resolveActiveLocale} from '@tyndo/hooks';
import {
 Button,
 Card,
 CardContent,
 EmptyState,
 GaugeBank,
 AlarmRibbon,
 Skeleton,
} from '@tyndo/ui-kit';
import {
 Activity,
 AlertTriangle,
 ChevronRight,
 Cpu,
 Factory,
 Globe2,
 LayoutGrid,
 MapPin,
} from 'lucide-react';

// ─── Wire shapes (defensively typed reads of the real gateway payloads) ────

interface ScadaOverview {
 equipmentCount?: number;
 tagCount?: number;
}

/** GET /multisite/sites — a row per configured site. */
interface SiteRow {
 id: string;
 code?: string;
 name?: string;
 timezone?: string | null;
 country?: string | null;
 isDefault?: boolean;
 isActive?: boolean;
}

/**
 * GET /scada/equipment-hierarchy/projection — denormalized ISA-95 tree with a
 * per-node runtime summary. Nodes that haven't reported runtime state carry
 * honest nulls (never fabricated numbers) — mirrored here.
 */
interface EquipmentNode {
 equipmentId: string;
 name?: string;
 area?: string | null;
 state?: string | null;
 availability?: number | null;
 activeAlarms?: number;
 oee?: number | null;
 tagCount?: number;
 children?: EquipmentNode[];
}

/** GET /production/oee?shift=current — plant-wide A×P×Q rollup. */
interface OeeRollup {
 runsConsidered?: number;
 source?: string;
 plant?: {
 availabilityPct?: number | null;
 performancePct?: number | null;
 qualityPct?: number | null;
 oeePct?: number | null;
 };
}

type CellState = 'running' | 'idle' | 'stopped' | 'error' | 'maintenance';

/** Map the gateway's normalized dashboard state onto the shell state tokens. */
function toCellState(state: string | null | undefined): CellState {
 const s = (state ?? '').toLowerCase();
 if (s.includes('run')) return 'running';
 if (s.includes('fault') || s.includes('error') || s.includes('abort')) return 'error';
 if (s.includes('maint') || s.includes('suspend')) return 'maintenance';
 if (s.includes('stop') || s.includes('off')) return 'stopped';
 return 'idle';
}

/**
 * The multisite list endpoint returns a bare array when sites exist, or an
 * explicit provenance envelope (`{ __source: 'empty', data: [] }`) when none
 * do. Accept both without inventing rows.
 */
function parseSites(raw: unknown): SiteRow[] {
 if (Array.isArray(raw)) return raw as SiteRow[];
 if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)) {
 return (raw as { data: SiteRow[] }).data;
 }
 return [];
}

/**
 * Tyndo Plant Canvas — the home page.
 *
 * Every number and every tile on this page is driven by real gateway data:
 * sites from /multisite/sites, the equipment tree + live states from
 * /scada/equipment-hierarchy/projection, OEE from the /production/oee rollup,
 * and alarm/work-order counts from their live sources. Where a tenant has no
 * data yet, each section renders an explicit empty state with a path to the
 * surface that creates it — never a fictional plant.
 */
export default function PlantCanvas() {
 const navigate = useNavigate();
 const { user} = useAuth();
 const liveAlarms = useAlarms();
 // Stable empty-array fallback: `useAlarms() ?? []` inline would mint a new
 // array identity every render and defeat the alarm-count memo below.
 const alarms = useMemo(() => liveAlarms ?? [], [liveAlarms]);

 const { data: scadaOverview} = useApiQuery<ScadaOverview | null>('/scada/overview');
 const { data: workOrders} = useApiQuery<Array<{ status: string}> | null>(
'/production/workorders'
 );
 const sitesQ = useApiQuery<unknown>('/multisite/sites');
 const hierarchyQ = useApiQuery<EquipmentNode[] | null>(
'/scada/equipment-hierarchy/projection'
 );
 const oeeQ = useApiQuery<OeeRollup | null>('/production/oee?shift=current');

 const equipmentCount = scadaOverview?.equipmentCount ?? null;
 const tagCount = scadaOverview?.tagCount ?? null;
 const activeWorkOrderCount =
 workOrders?.filter((w) => w.status ==='Released' || w.status ==='InProgress')
 .length ?? null;

 const sites = useMemo(() => parseSites(sitesQ.data), [sitesQ.data]);
 const hierarchy = useMemo(
 () => (Array.isArray(hierarchyQ.data) ? hierarchyQ.data : []),
 [hierarchyQ.data]
 );

 // Honest alarm severity split — Critical/Error/Warning are the real alarm
 // types on the live feed; no synthetic redistribution.
 const alarmCounts = useMemo(
 () => ({
 critical: alarms.filter((a) => a.type ==='Critical').length,
 major: alarms.filter((a) => a.type ==='Error').length,
 minor: alarms.filter((a) => a.type ==='Warning').length,
}),
 [alarms]
 );
 const activeAlarmCount = alarms.length;

 // OEE gauges — only metrics the rollup actually produced. Performance/OEE
 // are null when no run in the window carries an ideal-cycle standard, and
 // the whole bank is suppressed when no completed runs exist (0% would lie).
 const oeePlant = oeeQ.data?.plant;
 const runsConsidered = oeeQ.data?.runsConsidered ?? 0;
 const gaugeReadings = useMemo(() => {
 if (!oeePlant || runsConsidered === 0) return [];
 const mk = (id: string, label: string, value: number | null | undefined, sub: string) =>
 typeof value ==='number' && Number.isFinite(value)
 ? [{ id, label, value: Math.round(value), sub}]
 : [];
 return [
 ...mk('oee', 'OEE', oeePlant.oeePct, 'Current shift'),
 ...mk('avail', 'Availability', oeePlant.availabilityPct, 'Run vs planned time'),
 ...mk('perf', 'Performance', oeePlant.performancePct, 'Earned vs run time'),
 ...mk('qual', 'Quality', oeePlant.qualityPct, 'First-pass yield'),
 ];
}, [oeePlant, runsConsidered]);

 return (
 <div className="min-h-full bg-surface-base">
 <div className="mx-auto max-w-7xl px-6 py-6">
 {/* Eyebrow + Welcome */}
 <div className="mb-6 flex items-end justify-between">
 <div>
 <p className="t-eyebrow">Plant Canvas</p>
 <h1 className="mt-1 text-3xl font-semibold tracking-tight text-foreground">
 {greet(user?.name)}
 </h1>
 <p className="mt-1 text-sm text-muted-foreground">
 Live state across your sites and equipment — every figure below is
 real tenant data.
 </p>
 </div>
 <Button
 variant="outline"
 size="sm"
 onClick={() => navigate('/browse')}
 className="group text-xs"
 >
 <LayoutGrid className="h-3.5 w-3.5 text-muted-foreground" />
 Browse all modules
 <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
 </Button>
 </div>

 {/* Page-scoped alarm ribbon (only renders when alarms > 0) */}
 <AlarmRibbon
 counts={alarmCounts}
 scope="Across all sites"
 onSelect={() => navigate('/tyndo.alarms')}
 className="mb-6"
 />

 {/* OEE gauge bank — real /production/oee rollup for the current shift. */}
 <div className="mb-6">
 {oeeQ.isLoading ? (
 <Skeleton className="h-36 w-full rounded-lg" />
 ) : gaugeReadings.length > 0 ? (
 <GaugeBank readings={gaugeReadings} />
 ) : (
 <div className="rounded-lg border border-border bg-surface-raised p-4 text-sm text-muted-foreground">
 {oeeQ.isError
 ? 'OEE unavailable — the production rollup endpoint could not be reached.'
 : 'OEE unavailable — no completed production runs in the current shift. Figures appear here as soon as runs complete.'}
 </div>
 )}
 </div>

 {/* Stat ribbon — every value real, every gap an explicit dash. */}
 <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
 <StatCard
 Icon={Cpu}
 label="Equipment"
 value={fmt(equipmentCount)}
 sub={`${fmt(tagCount)} tags`}
 tone="phosphor"
 onClick={() => navigate('/tyndo.scada')}
 />
 <StatCard
 Icon={AlertTriangle}
 label="Active alarms"
 value={fmt(activeAlarmCount)}
 sub={activeAlarmCount === 0 ? 'All clear' : 'Tap to triage'}
 tone={activeAlarmCount > 0 ? 'critical' : 'phosphor'}
 onClick={() => navigate('/tyndo.alarms')}
 />
 <StatCard
 Icon={Factory}
 label="Work orders"
 value={fmt(activeWorkOrderCount)}
 sub="Released + In-Progress"
 tone="default"
 onClick={() => navigate('/tyndo.production')}
 />
 <StatCard
 Icon={Globe2}
 label="Sites"
 value={sitesQ.isLoading ? '…' : fmt(sites.length)}
 sub="Configured in Multi-Site"
 tone="default"
 onClick={() => navigate('/tyndo.multisite')}
 />
 </div>

 {/* Sites — the real /multisite/sites rows, or an explicit empty state. */}
 <section aria-label="Sites" className="mb-6">
 {sitesQ.isLoading ? (
 <Skeleton className="h-24 w-full rounded-lg" />
 ) : sites.length > 0 ? (
 <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
 {sites.map((site) => (
 <SiteCard
 key={site.id}
 site={site}
 onClick={() => navigate('/tyndo.multisite')}
 />
 ))}
 </div>
 ) : (
 <Card variant="elevated">
 <CardContent className="p-5">
 <EmptyState
 icon={Globe2}
 title="No sites configured"
 description="This tenant has no site rows yet. Define your sites in the Multi-Site module (or run the simulator to seed a fleet) and they will appear here."
 action={
 <Button size="sm" onClick={() => navigate('/tyndo.multisite')}>
 Open Multi-Site
 </Button>
 }
 />
 </CardContent>
 </Card>
 )}
 </section>

 {/* Equipment canvas — the real ISA-95 hierarchy with live states. */}
 <section aria-label="Equipment" className="space-y-6">
 {hierarchyQ.isLoading ? (
 <Skeleton className="h-48 w-full rounded-lg" />
 ) : hierarchy.length > 0 ? (
 hierarchy.map((root) => (
 <EquipmentPanel
 key={root.equipmentId}
 node={root}
 onNodeClick={() => navigate('/tyndo.scada/equipment_hierarchy')}
 />
 ))
 ) : (
 <Card variant="elevated">
 <CardContent className="p-5">
 <EmptyState
 icon={Factory}
 title="No equipment registered"
 description="No equipment hierarchy exists for this tenant yet. Define equipment in SCADA (or connect a PLC / run the simulator) to light this canvas up with live cell state."
 action={
 <Button size="sm" onClick={() => navigate('/tyndo.scada')}>
 Open SCADA
 </Button>
 }
 />
 </CardContent>
 </Card>
 )}
 </section>
 </div>
 </div>
 );
}

// ─── Subcomponents ───────────────────────────────────────────────────────

function SiteCard({ site, onClick}: { site: SiteRow; onClick: () => void}) {
 const meta = [site.country, site.timezone].filter(Boolean).join(' · ');
 return (
 <Button
 type="button"
 variant="ghost"
 onClick={onClick}
 className="h-auto whitespace-normal w-full justify-start text-left font-normal group flex items-start gap-3 rounded-lg border border-border bg-surface-raised p-4 text-left transition-all hover:bg-accent hover:shadow-sm"
 >
 <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
 <MapPin className="h-4 w-4" />
 </span>
 <span className="min-w-0">
 <span className="flex items-center gap-2">
 <span className="truncate text-sm font-medium text-foreground">
 {site.name ?? site.code ?? site.id}
 </span>
 {site.isDefault && (
 <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
 Default
 </span>
 )}
 {site.isActive === false && (
 <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
 Inactive
 </span>
 )}
 </span>
 <span className="mt-0.5 block truncate text-xs text-muted-foreground">
 {site.code ? `${site.code}${meta ? ' · ' : ''}` : ''}
 {meta}
 </span>
 </span>
 </Button>
 );
}

/**
 * One panel per hierarchy root (typically a site/area). Direct children render
 * as live cell tiles; a root with no children renders itself as a single tile.
 */
function EquipmentPanel({
 node,
 onNodeClick,
}: {
 node: EquipmentNode;
 onNodeClick: (node: EquipmentNode) => void;
}) {
 // Memoised so the `[node]` fallback doesn't mint a fresh array identity each
 // render and defeat the counts memo below.
 const cells = useMemo(
 () => (node.children && node.children.length > 0 ? node.children : [node]),
 [node],
 );
 const counts = useMemo(() => {
 const c: Record<CellState, number> = { running: 0, idle: 0, stopped: 0, error: 0, maintenance: 0};
 cells.forEach((cell) => { c[toCellState(cell.state)] += 1;});
 return c;
}, [cells]);

 return (
 <Card variant="elevated" className="overflow-hidden">
 <div className="flex items-center justify-between border-b border-border px-5 py-3">
 <div>
 <p className="t-eyebrow">{node.area || 'Equipment'}</p>
 <h2 className="mt-0.5 text-lg font-semibold tracking-tight text-foreground">
 {node.name ?? node.equipmentId}
 </h2>
 </div>
 <div className="flex items-center gap-2 text-xs">
 {counts.running > 0 && <Pill state="running" label={`${counts.running} running`} />}
 {counts.idle > 0 && <Pill state="idle" label={`${counts.idle} idle`} />}
 {counts.stopped > 0 && <Pill state="stopped" label={`${counts.stopped} stopped`} />}
 {counts.error > 0 && <Pill state="error" label={`${counts.error} fault`} />}
 {counts.maintenance > 0 && (
 <Pill state="maintenance" label={`${counts.maintenance} mtnc`} />
 )}
 </div>
 </div>
 <CardContent className="p-5">
 <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
 {cells.map((cell) => (
 <CellTile key={cell.equipmentId} cell={cell} onClick={() => onNodeClick(cell)} />
 ))}
 </div>
 </CardContent>
 </Card>
 );
}

function CellTile({ cell, onClick}: { cell: EquipmentNode; onClick: () => void}) {
 const state = toCellState(cell.state);
 const stateColor = `hsl(var(--state-${state}))`;
 const isLive = state ==='running';
 const childCount = cell.children?.length ?? 0;
 const detail = [
 typeof cell.oee ==='number' ? `OEE ${Math.round(cell.oee)}%` : null,
 (cell.activeAlarms ?? 0) > 0 ? `${cell.activeAlarms} alarms` : null,
 childCount > 0 ? `${childCount} assets` : null,
 ]
 .filter(Boolean)
 .join(' · ');
 return (
 <Button
 type="button"
 variant="ghost"
 onClick={onClick}
 className="h-auto whitespace-normal w-full justify-start text-left font-normal items-stretch group relative flex flex-col gap-2 rounded-lg border border-border bg-surface-raised p-3 text-left transition-all hover:bg-accent hover:border-border/80 hover:shadow-sm"
 >
 {/* State stripe */}
 <span
 aria-hidden
 className="absolute inset-x-0 top-0 h-0.5 rounded-t-lg"
 style={{ backgroundColor: stateColor}}
 />
 <div className="flex items-center justify-between">
 <span className="inline-flex items-center gap-1.5">
 <span
 aria-hidden
 className={`relative flex h-2 w-2 ${isLive ? 'tyndo-pulse' : ''}`}
 style={{ color: stateColor}}
 >
 <span
 className="relative inline-flex h-2 w-2 rounded-full"
 style={{ backgroundColor: stateColor}}
 />
 </span>
 <span className="text-[10px] font-semibold uppercase tracking-wider t-mono" style={{ color: stateColor}}>
 {state}
 </span>
 </span>
 <Activity className="h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
 </div>
 <div>
 <div className="text-sm font-medium text-foreground leading-tight">
 {cell.name ?? cell.equipmentId}
 </div>
 {detail && (
 <div className="mt-0.5 text-[11px] text-muted-foreground t-tabular">{detail}</div>
 )}
 </div>
 </Button>
 );
}

function StatCard({
 Icon,
 label,
 value,
 sub,
 tone,
 onClick,
}: {
 Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
 label: string;
 value: string;
 sub: string;
 tone:'default' |'phosphor' |'critical';
 onClick?: () => void;
}) {
 const accent =
 tone === 'phosphor'
 ? 'hsl(var(--data-phosphor))'
 : tone === 'critical'
 ? 'hsl(var(--alarm-critical))'
 : 'hsl(var(--muted-foreground))';

 return (
 <Button
 type="button"
 variant="ghost"
 onClick={onClick}
 disabled={!onClick}
 className="h-auto whitespace-normal w-full justify-start text-left font-normal items-stretch disabled:pointer-events-auto disabled:opacity-100 group relative flex flex-col gap-2 rounded-lg border border-border bg-surface-raised p-4 text-left transition-all hover:shadow-sm disabled:cursor-default disabled:hover:shadow-none"
 >
 <span
 aria-hidden
 className="absolute inset-x-0 top-0 h-0.5 rounded-t-lg"
 style={{ backgroundColor: accent}}
 />
 <div className="flex items-center justify-between">
 <p className="t-eyebrow">{label}</p>
 <Icon className="h-4 w-4" style={{ color: accent}} />
 </div>
 <div>
 <div className="t-display text-foreground">{value}</div>
 <div className="text-xs text-muted-foreground t-tabular">{sub}</div>
 </div>
 </Button>
 );
}

function Pill({
 state,
 label,
}: {
 state: CellState;
 label: string;
}) {
 const color = `hsl(var(--state-${state}))`;
 return (
 <span
 className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium t-mono uppercase tracking-wider"
 style={{
 backgroundColor: `hsl(var(--state-${state}) / 0.12)`,
 color,
 boxShadow: `inset 0 0 0 1px hsl(var(--state-${state}) / 0.3)`,
}}
 >
 <span
 className="h-1.5 w-1.5 rounded-full"
 style={{ backgroundColor: color}}
 />
 {label}
 </span>
 );
}

function fmt(n: number | null | undefined): string {
 return typeof n ==='number' ? n.toLocaleString(resolveActiveLocale()) :'—';
}

function greet(name?: string): string {
 const h = new Date().getHours();
 const greeting =
 h < 5 ? 'Good night' : h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
 return name ? `${greeting}, ${name.split(' ')[0]}` : greeting;
}
